import MeasureArea from '@apps-common/ui-icons/Area.jsx';
import CirclePlus from '@apps-common/ui-icons/CirclePlus.jsx';
import MeasureIcon from '@apps-common/ui-icons/Measure.jsx';
import PolygonPlus from '@apps-common/ui-icons/PolygonPlus.jsx';
import SquarePlus from '@apps-common/ui-icons/SquarePlus.jsx';
import UploadIcon from '@apps-common/ui-icons/Upload.jsx';
import MapPopper from '@apps-common/ui-maps/MapPopper';
import SquareIcon from '@mui/icons-material/CropDin';
import CircleIcon from '@mui/icons-material/RadioButtonUnchecked';

import {Box} from '@mui/material';
import {KEY_ESCAPE} from 'keycode-js';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import MapPopperButton from '../MapPopperButton.jsx';

import {FeatureItem} from './Feature.jsx';
import {aoiTooltipMap} from './tooltips.js';
import {fitExtent, setDrawTool} from '@app/actions/map';
import {showModal} from '@app/actions/ui';
import {trackEvent} from '@app/analytics';
import {DownloadAoiDialog} from '@app/components/Dialogs/DownloadAOI';
import {MAP_DRAW_LAYERS, MAP_TOOLS, MODALS} from '@app/constants';
import useOnKeyDown from '@app/hooks/useOnKeyDown';

const sortFeatures = (a, b) => {
  return a.id < b.id ? 1 : -1;
};

const drawTools = [
  {
    name: MAP_TOOLS.DRAW_LOCKED_CIRCLE,
    dataQe: 'draw-circle-locked',
    icon: <CircleIcon />,
  },
  {
    name: MAP_TOOLS.DRAW_LOCKED_RECT,
    dataQe: 'draw-rectangle-locked',
    icon: <SquareIcon />,
  },
  {
    name: MAP_TOOLS.DRAW_POLYGON,
    dataQe: 'draw-polygon',
    icon: {
      [MAP_DRAW_LAYERS.MEASURE]: <MeasureArea />,
      default: <PolygonPlus />,
    },
  },
  {
    name: MAP_TOOLS.DRAW_CIRCLE,
    dataQe: 'draw-circle',
    icon: <CirclePlus />,
  },
  {
    name: MAP_TOOLS.DRAW_RECT,
    dataQe: 'draw-rectangle',
    icon: <SquarePlus />,
  },
  {
    name: MAP_TOOLS.DRAW_LINE,
    dataQe: 'draw-line',
    icon: <MeasureIcon />,
  },
];

const DrawTools = ({
  drawTool = {},
  drawLayer,
  setDrawTool,
  showModal,
  fitExtent,
  open,
  anchorEl,
  features,
  title = null,
  tempGeometry,
  setTempGeometry,
  toolsAvailable = [
    MAP_TOOLS.DRAW_LOCKED_CIRCLE,
    MAP_TOOLS.DRAW_LOCKED_RECT,
    MAP_TOOLS.DRAW_POLYGON,
    MAP_TOOLS.DRAW_CIRCLE,
    MAP_TOOLS.DRAW_RECT,
  ],
  onDelete,
  uploadAvailable,
  tooltips = aoiTooltipMap,
  canToggleVisibility = true,
  onChangeVisibility,
  onHover = () => {},
  onSave,
}) => {
  const [downloadFeature, setDownloadFeature] = useState(null);
  const listRef = useRef();

  useEffect(() => {
    // ensure the draw tool is fresh when this is opened.
    if (open) {
      setDrawTool({
        name: MAP_TOOLS.NONE,
        layer: drawLayer,
        target: '',
      });
    }

    return () => {
      // reset on close as well.
      setDrawTool({
        name: MAP_TOOLS.NONE,
        layer: MAP_DRAW_LAYERS.NONE,
        target: '',
      });
    };
  }, [setDrawTool, drawLayer, open]);

  const onToolClick = (toolName, targetId = '') => {
    const active =
      toolName !== drawTool.name ||
      (toolName === MAP_TOOLS.EDIT && drawTool.target !== targetId);

    // this needs cleared before starting to prevent
    //  showing the artifact of the previous feature.
    setTempGeometry(null);

    if (active) {
      setDrawTool({
        name: toolName,
        target: targetId,
        layer: drawLayer,
      });
    } else {
      setDrawTool({
        name: MAP_TOOLS.NONE,
        target: '',
        layer: MAP_DRAW_LAYERS.NONE,
      });
    }
    trackEvent('Draw Tools', {
      action: 'Clicked',
      drawTool: toolName,
      drawLayer,
    });
  };

  const onKeyDown = () => {
    setDrawTool({
      name: MAP_TOOLS.NONE,
      layer: MAP_DRAW_LAYERS.NONE,
      target: '',
    });
    trackEvent('Draw Tools', {
      action: 'Pressed escaped key',
      drawTool: drawTool.name,
      drawLayer,
    });
  };
  useOnKeyDown(KEY_ESCAPE, onKeyDown);

  const onUploadClick = () => {
    showModal(MODALS.UPLOAD);
    trackEvent('Draw Tools', {
      action: 'Clicked upload button',
      drawTool: 'Upload',
    });
  };

  const defaultTitle = 'Area of Interest';

  return (
    <MapPopper
      title={title || defaultTitle}
      open={open}
      anchorEl={open ? anchorEl : null}
      placement="left-end"
      ariaLabel="drawTools"
      data-testid="draw-tools"
      sx={{width: 330}}
      onClose={() => {}}
    >
      {downloadFeature && (
        <DownloadAoiDialog
          open
          aoi={downloadFeature}
          onClose={() => setDownloadFeature(null)}
          title="Download map feature"
          subtitle="Drawing will be downloaded as GeoJSON."
        />
      )}
      <Box ref={listRef} sx={{maxHeight: 300, overflow: 'auto'}}>
        {!!tempGeometry &&
          drawTool.name !== MAP_TOOLS.NONE &&
          drawTool.name !== MAP_TOOLS.EDIT && (
            <FeatureItem
              key="temp"
              tempGeometry={tempGeometry}
              tooltips={tooltips}
              isEditing
            />
          )}

        {features
          .filter(feature => !!feature)
          .sort(sortFeatures)
          .map(feature => (
            <FeatureItem
              tooltips={tooltips}
              key={feature.id}
              canToggleVisibility={canToggleVisibility}
              onHover={onHover}
              onDelete={() => {
                onDelete(feature);
                // reset the map tools
                setDrawTool({
                  name: MAP_TOOLS.NONE,
                  layer: MAP_DRAW_LAYERS.NONE,
                  target: '',
                });
                trackEvent('Draw Tools', {
                  action: 'Deleted',
                  drawLayer,
                });
              }}
              onDownload={() => {
                setDownloadFeature({...feature});
                trackEvent('Draw Tools', {
                  action: 'Downloaded',
                  drawLayer,
                });
              }}
              feature={feature}
              tempGeometry={tempGeometry}
              geometryId={feature.id}
              isEditing={
                drawTool.target === feature.id &&
                drawTool.name === MAP_TOOLS.EDIT
              }
              onEdit={() => {
                onToolClick(MAP_TOOLS.EDIT, feature.id);
                trackEvent('Draw Tools', {
                  action: 'Edited',
                  drawLayer,
                });
              }}
              onChangeVisibility={() => {
                onChangeVisibility(feature);
              }}
              onSave={() => {
                onSave(feature);
                setDrawTool({
                  name: MAP_TOOLS.NONE,
                  layer: MAP_DRAW_LAYERS.NONE,
                  target: '',
                });
              }}
              onZoomTo={extent => {
                fitExtent(extent);
                trackEvent('Draw Tools', {
                  action: 'Zoom to feature',
                  drawLayer,
                });
              }}
            />
          ))}
      </Box>
      {uploadAvailable && (
        <MapPopperButton
          key="uploadTool"
          tooltip="Import file to define area of interest"
          tooltipPosition="top-end"
          onClick={onUploadClick}
          data-testid="import-tool"
        >
          <UploadIcon />
        </MapPopperButton>
      )}

      {drawTools
        .filter(tool => toolsAvailable.includes(tool.name))
        .map(tool => (
          <MapPopperButton
            key={tool.name}
            tooltip={tooltips[tool.name]}
            tooltipPosition="top-end"
            color={
              drawTool.name === tool.name && drawTool.layer === drawLayer
                ? 'primary'
                : undefined
            }
            onClick={() => onToolClick(tool.name)}
            data-testid={tool.dataQe}
          >
            {/* this cascades a default icon. */}
            {tool.icon[drawLayer] || tool.icon.default || tool.icon}
          </MapPopperButton>
        ))}
    </MapPopper>
  );
};

DrawTools.propTypes = {
  drawTool: PropTypes.object,
  setDrawTool: PropTypes.func,
  showModal: PropTypes.func,
  open: PropTypes.bool,
  anchorEl: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.instanceOf(Element),
  ]),
  drawLayer: PropTypes.string,
  title: PropTypes.string,
  tempGeometry: PropTypes.object,
  canToggleVisibility: PropTypes.bool,
  tooltips: PropTypes.object,
  onDelete: PropTypes.func,
  toolsAvailable: PropTypes.array,
  uploadAvailable: PropTypes.bool,
  fitExtent: PropTypes.func,
  onChangeVisibility: PropTypes.func,
  onHover: PropTypes.func,
  onSave: PropTypes.func,
  features: PropTypes.array,
  setTempGeometry: PropTypes.func,
};

const mapStateToProps = state => ({
  drawTool: state.map.drawTool,
});

const mapDispatchProps = {
  setDrawTool,
  showModal,
  fitExtent,
};

export default connect(mapStateToProps, mapDispatchProps)(DrawTools);
